<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getStateName } from '@/libs/utils/workflows.js'
import { getWorkflow, getWorkflows } from '@/services/workflowService.js'
import { deletePollTemplateCampaign, getPollTemplate, getPollTemplateCampaigns } from '@/services/pollTemplateService.js'
import { formatCampaignAttribute } from '@/libs/utils/translations.js'

import Loading from '@/components/Generics/Loadings/Loading.vue'
import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'
import Paginazione from '@/components/Generics/Paginations/Paginazione.vue'

import { usePageNumberStore } from '@/services/store.js'
import { campaignStates } from '@/services/campaignService.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowLeft, faListCheck, faTrash } from '@fortawesome/free-solid-svg-icons'
import TranslationProvider from '@/provider/translations.js'

library.add(faArrowLeft)
library.add(faTrash)
library.add(faListCheck)

const props = defineProps(['updatePageTitle'])
const route = useRoute()
const router = useRouter()
const pageNumberStore = usePageNumberStore()

const loading = ref(true)
const campaigns = ref([])
const pollTemplate = ref(null)
const canDelete = ref(false)
const canView = ref(false)

const modal = ref({
  show: false,
  title: '',
  body: '',
  fn: null,
  fnArgs: null,
  isDelete: false
})

const pagination = ref({
  nPages: 1,
})

const workflows = ref([])

onMounted(async () => {
  canDelete.value = route.meta.permissions.canDelete ?? false
  canView.value = route.meta.permissions.canView ?? false

  if (!canView.value) {
    return
  }

  pollTemplate.value = await getPollTemplate(route.params.id, {complete: 1})
  props.updatePageTitle('Lista campagne - ' + pollTemplate.value.title)

  let page = pageNumberStore.getPage(route.name) ?? 1
  const WorkflowList = await getWorkflows()

  for(const workflow of WorkflowList) {
    const wf = await getWorkflow(workflow.key)
    workflows.value.push(wf.data)
  }

  await getCampaigns(page)
})

async function getCampaigns(page = 1) {
  loading.value = true
  campaigns.value = []

  const response = await getPollTemplateCampaigns(pollTemplate.value.reference, page)
  campaigns.value = response.data

  if (response.pagination) {
    calculatePagination(response.pagination)
  }

  loading.value = false
}

const calculatePagination = (pageData) => {
  pagination.value = pageData
  pagination.value.nPages = Math.ceil(pageData.total_items / pageData.items_per_page)
}

async function goToPage(page) {
  campaigns.value = []
  await getCampaigns(page)
}

async function deleteCampaign(campaign) {
  loading.value = true
  await deletePollTemplateCampaign(campaign.id)

  closeModal()

  await getCampaigns()
}

function showModal(title, body, fn, fnArgs, isDelete = false) {
  modal.value.fn = fn
  modal.value.fnArgs = fnArgs
  modal.value.title = title
  modal.value.body = body
  modal.value.show = true
  modal.value.isDelete = isDelete
}

function closeModal() {
  modal.value.fn = null
  modal.value.fnArgs = null
  modal.value.title = ''
  modal.value.body = ''
  modal.value.show = false
  modal.value.isDelete = false
}

</script>

<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 px-0">
        <div class="card shadow-none">
          <div class="card-body">

            <loading v-if="loading"></loading>

            <div v-else class="table-responsive">

              <table class="table table-bordered table-striped">
                <thead>
                <tr>
                  <th>N° revisione</th>
                  <th>Fixed</th>
                  <th>Stato</th>
                  <th>Numero compilazioni</th>
                  <th>Attributi</th>
                  <th>Azioni</th>
                </tr>
                </thead>
                <tbody>
                <template v-if="campaigns.length">
                  <tr v-for="(campaign, index) in campaigns" :key="index">
                    <td>
                      {{ campaign.pollTemplateRevisionNumber }}
                    </td>
                    <td>
                      {{ campaign.fixed ? 'Sì' : 'No' }}
                    </td>
                    <td>
                      {{ campaignStates[campaign.state] }}
                    </td>
                    <td>
                      <div v-if="campaign?.stats">
                        <p v-for="(num, state) in campaign.stats" :key="state" class="mb-0">
                          {{ getStateName(state, workflows) }}: {{ num }}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div>
                        <p v-for="(value, attribute) in campaign.attributes" :key="attribute" class="mb-0">
                          <b>{{ TranslationProvider.getTrans(`campaign.attribute.${attribute}`, attribute) }}</b>:
                          {{formatCampaignAttribute(attribute, value)}}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex justify-content-end">
                        <router-link :to="{name: 'admin/PollTemplateCampaignStats', params: {id: pollTemplate.id} , query: {idCampaign: campaign.id} }">
                          <button :disabled="campaign.filledNumber <= 0"
                                  class="btn btn-primary btn-sm ms-2"
                                  title="Visualizza somministrazioni"
                          >
                            <font-awesome-icon icon="fa-solid fa-list-check"></font-awesome-icon>
                          </button>
                        </router-link>
                        <button v-if="canDelete" class="btn btn-danger btn-sm ms-2"
                                title="Elimina campagna"
                                @click="showModal('Elimina campagna', 'La campagna verrà eliminata, continuare?', deleteCampaign, campaign, true)"
                        >
                          <font-awesome-icon icon="fa-solid fa-trash"></font-awesome-icon>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-else>
                  <td colspan="6" class="text-center">
                    Nessun risultato
                  </td>
                </tr>
                </tbody>
              </table>

              <div v-if="pagination.nPages > 1" class="d-flex justify-content-end m-2 mt-4">
                <Paginazione
                  :total-pages="pagination.nPages"
                  @page-changed="goToPage"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between mt-3">
        <button-torna-alla-lista @click="router.back()"></button-torna-alla-lista>
      </div>

    </div>
  </div>

  <div :class="'modal' + (modal.show ? ' d-block' : '')" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ modal.title }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Chiudi"
                  @click="closeModal"></button>
        </div>
        <div class="modal-body" v-html="modal.body">
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" @click="closeModal">Annulla</button>
          <button type="button"
                  class="btn"
                  :disabled="loading"
                  :class="modal.isDelete ? 'btn-danger' : 'btn-primary'"
                  @click="modal.fn(modal.fnArgs)"
          >
           {{modal.isDelete ? 'Elimina' : 'Conferma'}}
          </button>
        </div>
      </div>
    </div>
  </div>

</template>
